import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LabelInput } from '@/components/ui/inputs';
import { TextArea } from '@/components/ui/inputs/TextArea';
import { Selector } from '@/components/ui/inputs/selector';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { Text } from '@/components/ui/text/Text';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { SidePanelFormLayout, PermissionGuard } from '@/components/layout';
import { useCreateSpecialLeave } from '@/hooks/absence/useCreateSpecialLeave';
import { HALF_DAY_WORKDAY_TYPES } from '@/api/domains/absence/absence.types';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import { colors } from '@/assets/styles/colors';
import { CreateFractionalLeaveForm } from './CreateFractionalLeaveForm';


const HalfDayCountHint: React.FC = () => {
  const { t: translate } = useTranslation('absence');

  return (
    <Text variant="small-regular" color={colors.important.main}>
      {translate('specialLeave.form.halfDayCountHint')}
    </Text>
  );
};

const FractionOnlyHint: React.FC = () => {
  const { t: translate } = useTranslation('absence');
  const anchorRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div
      className="relative flex items-center gap-1 mt-1"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div ref={anchorRef} className="flex items-center gap-1 cursor-help">
        <Text variant="small-regular" color={colors.grays.neutral66}>
          {translate('specialLeave.form.fractionOnlyHint')}
        </Text>
      </div>
      {showTooltip && (
        <Tooltip
          body={translate('specialLeave.form.fractionOnlyTooltip')}
          anchorRef={anchorRef}
          strategy="auto"
          placement="top"
          maxWidth={300}
        />
      )}
    </div>
  );
};

export const CreateSpecialLeaveForm: React.FC = () => {
  const { t: translate } = useTranslation('absence');

  const {
    form,
    isSubmitted,
    isSubmitting,
    handleSubmit,
    employeeOptions,
    isSelectedEmployeeSynced,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    handleEmployeeSearch,
    handleEmployeeChange,
    loadMoreEmployees,
    permitType,
    permitTypeOptions,
    handlePermitTypeChange,
    specialLeaveTypeOptions,
    isLoadingSpecialLeaveTypes,
    workdayTypeOptions,
    isLoadingWorkdayTypes,
  } = useCreateSpecialLeave();

  const startDateValue = form.watch('startDate');
  const workdayTypeValue = form.watch('workdayType');
  const dayCountValue = Number(form.watch('dayCount'));
  const showHalfDayCountHint = HALF_DAY_WORKDAY_TYPES.has(workdayTypeValue) && dayCountValue >= 1;

  return (
    <SidePanelFormLayout onSubmit={handleSubmit} data-testid="create-special-leave-form">
      <SidePanelFormLayout.Fields>
        <Selector
          label={translate('specialLeave.form.employee')}
          placeholder={translate('specialLeave.form.employeePlaceholder')}
          options={employeeOptions}
          value={form.watch('employeeTargetId') ? String(form.watch('employeeTargetId')) : undefined}
          onChange={handleEmployeeChange}
          searchable
          onSearchChange={handleEmployeeSearch}
          hasNextPage={employeeHasMore}
          onLoadMore={loadMoreEmployees}
          isLoading={isLoadingEmployees}
          isLoadingMore={isLoadingMoreEmployees}
          disabled={isSubmitting}
          color={form.errors.employeeTargetId && isSubmitted ? 'error' : 'default'}
          supportingText={
            form.errors.employeeTargetId && isSubmitted
              ? translate(form.errors.employeeTargetId.message as string)
              : undefined
          }
          required
          data-testid="employee-selector"
        />

        <div className="flex flex-col gap-1">
          <Selector
            label={translate('specialLeave.form.permitType')}
            placeholder={translate('specialLeave.form.permitTypePlaceholder')}
            options={permitTypeOptions}
            value={permitType || undefined}
            onChange={handlePermitTypeChange}
            disabled={isSubmitting}
            required
            data-testid="permit-type-selector"
          />
          {isSelectedEmployeeSynced && <FractionOnlyHint />}
        </div>

        {permitType === 'special' && (
          <>
            <Selector
              label={translate('specialLeave.form.specialLeaveType')}
              placeholder={translate('specialLeave.form.specialLeaveTypePlaceholder')}
              options={specialLeaveTypeOptions}
              value={form.watch('specialLeaveTypeId') ? String(form.watch('specialLeaveTypeId')) : undefined}
              onChange={(value) => {
                const numericValue = normalizeNumericSelectValue(value);
                if (numericValue !== undefined) {
                  form.setValue('specialLeaveTypeId', numericValue, { shouldDirty: true });
                }
              }}
              disabled={isSubmitting || isLoadingSpecialLeaveTypes}
              color={form.errors.specialLeaveTypeId && isSubmitted ? 'error' : 'default'}
              supportingText={
                form.errors.specialLeaveTypeId && isSubmitted
                  ? translate(form.errors.specialLeaveTypeId.message as string)
                  : undefined
              }
              required
              data-testid="special-leave-type-selector"
            />

            <Selector
              label={translate('specialLeave.form.workdayType')}
              placeholder={translate('specialLeave.form.workdayTypePlaceholder')}
              options={workdayTypeOptions}
              value={form.watch('workdayType') || undefined}
              onChange={(value) => {
                const codeValue = Array.isArray(value) ? value[0] : value;
                if (codeValue) {
                  form.setValue('workdayType', codeValue, { shouldDirty: true });
                }
              }}
              disabled={isSubmitting || isLoadingWorkdayTypes}
              color={form.errors.workdayType && isSubmitted ? 'error' : 'default'}
              supportingText={
                form.errors.workdayType && isSubmitted
                  ? translate(form.errors.workdayType.message as string)
                  : undefined
              }
              required
              data-testid="workday-type-selector"
            />

            <div className="flex flex-col gap-1">
              <LabelInput
                label={translate('specialLeave.form.dayCount')}
                type="number"
                registration={form.register('dayCount')}
                disabled={isSubmitting}
                color={form.errors.dayCount && isSubmitted ? 'error' : undefined}
                supportingText={
                  form.errors.dayCount && isSubmitted
                    ? translate(form.errors.dayCount.message as string)
                    : undefined
                }
                required
                data-testid="day-count-input"
              />
              {showHalfDayCountHint && <HalfDayCountHint />}
            </div>

            <DatePicker
              label={translate('specialLeave.form.startDate')}
              value={form.watch('startDate') || undefined}
              onChange={(date) => form.setValue('startDate', date, { shouldDirty: true })}
              disabled={isSubmitting}
              color={form.errors.startDate && isSubmitted ? 'error' : undefined}
              iconColor={colors.feedback.success300}
              supportingText={
                form.errors.startDate && isSubmitted
                  ? translate(form.errors.startDate.message as string)
                  : undefined
              }
              required
              data-testid="start-date-picker"
            />

            <DatePicker
              label={translate('specialLeave.form.endDate')}
              value={form.watch('endDate') || undefined}
              disabled
              minDate={startDateValue || undefined}
              iconColor={colors.feedback.error300}
              data-testid="end-date-picker"
            />

            <DatePicker
              label={translate('specialLeave.form.returnDate')}
              value={form.watch('returnDate') || undefined}
              disabled
              data-testid="return-date-picker"
            />

            <TextArea
              label={translate('specialLeave.form.detail')}
              placeholder={translate('specialLeave.form.detailPlaceholder')}
              registration={form.register('detail')}
              disabled={isSubmitting}
              maxLength={300}
              showCharacterCount
              supportingText={
                form.errors.detail && isSubmitted
                  ? translate(form.errors.detail.message as string)
                  : undefined
              }
              data-testid="detail-textarea"
            />
          </>
        )}
      </SidePanelFormLayout.Fields>

      {permitType === 'special' && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard action="create">
            <PrimaryButton
              type="submit"
              fullWidth
              isLoading={isSubmitting}
              disabled={isSubmitting}
              data-testid="submit-special-leave-button"
            >
              {translate('specialLeave.form.submit')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}

      {permitType === 'fraction' && (
        <CreateFractionalLeaveForm employeeTargetId={form.watch('employeeTargetId')} />
      )}
    </SidePanelFormLayout>
  );
};

export default CreateSpecialLeaveForm;
